import { useMemo } from 'react';
import { TFunction } from 'i18next';
import {
    Control,
    FieldErrors,
    UseFormRegister,
    useFieldArray,
    useWatch,
} from 'react-hook-form';
import { v4 as uuidv4 } from 'uuid';
import Input from 'src/components/FormComponents/Input';
import { TextIcon } from 'src/components/Icons/TextIcon';
import { PlusIcon } from 'src/components/Icons/icons';
import {
    OrganizationFormValues,
    OrganizationHierarchyFormValue,
} from './createOrganizationForm.schema';
import { useSignedInUser } from 'src/view/hooks/useSignedInUser';
import { isUserAdmin } from 'src/domain/user';

interface HierarchySectionProps {
    control: Control<OrganizationFormValues>;
    register: UseFormRegister<OrganizationFormValues>;
    errors: FieldErrors<OrganizationFormValues>;
    t: TFunction;
    hierarchy: OrganizationHierarchyFormValue[];
    readOnly?: boolean;
}

export const OrganizationHierarchySection = ({
    control,
    register,
    errors,
    t,
    hierarchy,
    readOnly = false,
}: HierarchySectionProps) => {
    const user = useSignedInUser();
    const canEdit = isUserAdmin(user) && !readOnly;

    const { fields, append, remove } = useFieldArray({
        control,
        name: 'hierarchy',
        keyName: 'fieldId',
    });

    const watchedHierarchy = useWatch({
        control,
        name: 'hierarchy',
    }) as OrganizationHierarchyFormValue[] | undefined;

    const buildLevelValue = (index: number): OrganizationHierarchyFormValue => {
        const watched = watchedHierarchy?.[index];
        const fallback = hierarchy?.[index];
        return (
            watched ??
            fallback ?? {
                id: fields[index]?.id,
                name: '',
                parentId: undefined,
            }
        );
    };

    const groupedEntries = useMemo(() => {
        return fields.map((field, index) => {
            const value = buildLevelValue(index);
            return {
                field,
                index,
                value,
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fields, watchedHierarchy]);

    const childrenLookup = useMemo(() => {
        const map = new Map<string, typeof groupedEntries>();
        groupedEntries.forEach(entry => {
            const parentId = entry.value.parentId;
            if (!parentId) {
                return;
            }
            const current = map.get(parentId) ?? [];
            current.push(entry);
            map.set(parentId, current);
        });
        return map;
    }, [groupedEntries]);

    const collectDescendantIndices = (levelId?: string): number[] => {
        if (!levelId) return [];
        const children = childrenLookup.get(levelId) ?? [];
        const childIndices = children.map(child => child.index);
        children.forEach(child => {
            childIndices.push(
                ...collectDescendantIndices(child.value.id ?? child.field.id),
            );
        });
        return childIndices;
    };

    const rootNodes = useMemo(() => {
        return groupedEntries.filter(entry => !entry.value.parentId);
    }, [groupedEntries]);

    const handleAddLevel = (parentId?: string) =>
        append({
            id: uuidv4(),
            name: '',
            parentId,
        });

    const renderLevelNode = (
        entry: typeof groupedEntries[number],
    ): JSX.Element => {
        const children = childrenLookup.get(entry.value.id ?? '') ?? [];

        return (
            <div key={entry.field.fieldId} className="space-y-3">
                <div className="rounded-2xl border border-starkWhite bg-white p-4 shadow-sm">
                    <input
                        type="hidden"
                        value={entry.value.id ?? ''}
                        {...register(`hierarchy.${entry.index}.id` as const)}
                    />
                    <input
                        type="hidden"
                        value={entry.value.parentId ?? ''}
                        {...register(
                            `hierarchy.${entry.index}.parentId` as const,
                        )}
                    />
                    <div className="flex gap-3">
                        {/* <Input
                            className="text-input"
                            label={t(
                                'admin.forms.createOrganization.hierarchyLevelId',
                            )}
                            disabled
                            error={errors.hierarchy?.[entry.index]?.id}
                            {...register(
                                `hierarchy.${entry.index}.id` as const,
                            )}
                        /> */}
                        <Input
                            className="text-input"
                            containerClassName="flex-1"
                            label={t(
                                'admin.forms.createOrganization.hierarchyLevelName',
                            )}
                            error={errors.hierarchy?.[entry.index]?.name}
                            readOnly={readOnly}
                            {...register(
                                `hierarchy.${entry.index}.name` as const,
                            )}
                        />
                    </div>
                    {canEdit && (
                        <div className="mt-3 flex flex-wrap items-center gap-2 text-xs text-mongooseGray">
                            <button
                                type="button"
                                className="text-brightOrange underline"
                                onClick={() => handleAddLevel(entry.value.id)}
                            >
                                {t(
                                    entry.value.parentId
                                        ? 'admin.forms.createOrganization.addHierarchySublevel'
                                        : 'admin.forms.createOrganization.addHierarchyLevel',
                                )}
                            </button>
                            <button
                                type="button"
                                className="text-venetianRed underline"
                                onClick={() => {
                                    const descendantIndices =
                                        collectDescendantIndices(
                                            entry.value.id ?? entry.field.id,
                                        );
                                    const toRemove = [
                                        entry.index,
                                        ...descendantIndices,
                                    ].sort((a, b) => b - a);
                                    remove(toRemove);
                                }}
                            >
                                {t(
                                    'admin.forms.createOrganization.removeHierarchyLevel',
                                )}
                            </button>
                        </div>
                    )}
                </div>
                {children.length > 0 && (
                    <div className="ml-5 border-l border-starkWhite pl-4 space-y-3">
                        {children.map(child => renderLevelNode(child))}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="space-y-3">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
                <h3 className="text-lg font-semibold">
                    {t('admin.forms.createOrganization.hierarchyTitle')}
                </h3>
                {canEdit && (
                    <button
                        type="button"
                        className="primary-btn-small flex items-center gap-1 pl-2 pr-4 py-1"
                        onClick={() => handleAddLevel()}
                    >
                        <TextIcon SVG={PlusIcon} />
                        <span>
                            {t(
                                'admin.forms.createOrganization.addHierarchyLevel',
                            )}
                        </span>
                    </button>
                )}
            </div>

            {fields.length === 0 && (
                <p className="text-sm text-mongooseGray">
                    {t('admin.forms.createOrganization.hierarchyEmpty')}
                </p>
            )}

            <div className="space-y-4">
                {rootNodes.map(node => renderLevelNode(node))}
            </div>
        </div>
    );
};
